import {ApolloError, useQuery} from '@apollo/client';
import {useMemo} from 'react';

import {
  PREDICTION_RUNS_POLL_INTERVAL_MS,
  splitPredictionRuns,
  type NormalizedRun,
} from 'model-hub/prediction-runs/splitPredictionRuns';
import {type ScientificModelsByExternalReference} from 'model-hub/scientific-models/ScientificModelMetadata';

import type {
  MoleculePropertyPredictionJobsQuery,
  MoleculePropertyPredictionJobsQueryVariables,
} from './__generated/MoleculePropertyPredictionJobsQuery';
import {MOLECULE_PROPERTY_PREDICTION_JOBS_QUERY} from './MoleculePredictedPropertiesQueries';

function asPropertyMap(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

export function useGetPropertyPredictionRuns({
  catalogByExternalReference,
  moleculeId,
}: {
  catalogByExternalReference: ScientificModelsByExternalReference;
  moleculeId: string;
}): {
  completedRuns: Array<NormalizedRun>;
  error: ApolloError | undefined;
  loading: boolean;
  pendingRuns: Array<NormalizedRun>;
  refetch: () => void;
} {
  const result = useQuery<MoleculePropertyPredictionJobsQuery, MoleculePropertyPredictionJobsQueryVariables>(
    MOLECULE_PROPERTY_PREDICTION_JOBS_QUERY,
    {
      variables: {moleculeId},
      pollInterval: PREDICTION_RUNS_POLL_INTERVAL_MS,
    }
  );

  const runs: Array<NormalizedRun> = useMemo(
    () =>
      (result.data?.moleculePropertyPredictionJobs ?? []).map((job) => {
        const model = catalogByExternalReference.get(job.modelName);
        return {
          id: job.id,
          status: job.statusCode,
          createdAt: job.createdAt,
          modelName: job.modelName,
          displayName: model?.displayName ?? job.modelName,
          iconPath: model?.iconPath,
          properties: asPropertyMap(job.properties),
        };
      }),
    [catalogByExternalReference, result.data]
  );

  const {pendingRuns, completedRuns} = useMemo(() => splitPredictionRuns(runs), [runs]);

  return {
    pendingRuns,
    completedRuns,
    loading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}
